import moment from "moment";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { fetchCsvData } from "../../../api/getCalls/fetchCsvData";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { InfoIcon } from "../../../svg/info-icon";
import CsvTable, { CsvData } from "../../csvTable/csvTable";
import PdfRender from "../../pdfRender/pdfRender";
import { usePdfFetchingFromFilepath } from "./hooks/usePdfFetchingFromFilepath";

const NarGridModal = (props: { onClose: () => void }) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const { setTableDataForContext } = useTablesContext();
  const [csvData, setCsvData] = useState<CsvData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<"csv" | "pdf" | null>(null);
  const {
    downloadUrl,
    loading: pdfLoading,
    fetchDownloadUrl,
  } = usePdfFetchingFromFilepath();

  useEffect(() => {
    let isMounted = true;
    const loadFileData = async () => {
      if (!metaData?.source) return;

      const fileExtension = metaData.source.toLowerCase().split(".").pop();
      const detectedFileType =
        fileExtension === "csv"
          ? "csv"
          : fileExtension === "pdf"
            ? "pdf"
            : null;

      setFileType(detectedFileType);

      if (detectedFileType === "pdf") {
        await fetchDownloadUrl(metaData?.source);
        if (!isMounted) {
          return;
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetchCsvData(metaData.source);
        if (!isMounted) {
          return;
        }
        if (response.success && response.data) {
          Papa.parse(response.data, {
            complete: (results) => {
              if (results.data && results.data.length > 0) {
                const headers = results.data[0] as string[];
                const rows = results.data.slice(1) as string[][];
                setCsvData({ headers, rows });
              }
            },
            header: false,
            skipEmptyLines: true,
            error: (error) => {
              setError(`CSV parsing error: ${error.message}`);
            },
          });
        } else {
          setError(response.error || "Failed to fetch CSV data");
        }
      } catch (err) {
        setError(
          `Error loading CSV: ${err instanceof Error ? err.message : "Unknown error"}`,
        );
      } finally {
        setLoading(false);
      }
    };

    loadFileData();
    return () => {
      isMounted = false;
    };
  }, [metaData?.source]);

  useEffect(() => {
    return () => {
      setTableDataForContext("csv-table", {
        currentStartPage: 1,
        startIndex: 0,
        endIndex: 14,
        searchedText: "",
        sortAscending: true,
        currentActiveArrayIndex: 0,
        lastBatchFetched: 0,
      });
    };
  }, []);

  return (
    <Modal
      dialogId={ModalId.NAR_GRID_MODAL}
      title="NAR Grid"
      onSave={props.onClose}
      saveButtonText="Close"
      cancelText="Cancel"
      onCancel={props.onClose}
      onClose={props.onClose}
      showSingleButton={true}
      heightPercentage={90}
      hideFooter={true}
    >
      {metaData?.loading || pdfLoading ? (
        <div className="flex-rwo flex h-full items-center justify-center gap-3">
          <SpinningLoader />
          <span>Loading NAR Grid...</span>
        </div>
      ) : metaData?.source ? (
        <div className="flex h-full flex-col pb-4">
          <div className="items-start justify-start pb-2 text-sm font-bold">
            {`Updated At: ${moment(metaData?.created_at).format("MM/DD/YYYY")}`}
          </div>
          {fileType === "csv" && (
            <CsvTable
              csvData={csvData || { headers: [], rows: [] }}
              className="h-full w-full overflow-y-auto"
              loading={loading}
              error={error}
              emptyMessage="No NAR Grid data available"
            />
          )}
          {fileType === "pdf" && <PdfRender file={downloadUrl} />}
        </div>
      ) : (
        <NoSourceAvailable />
      )}
    </Modal>
  );
};

export default NarGridModal;

const NoSourceAvailable = () => {
  return (
    <div className="no-source-available flex h-full flex-col items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-2 text-lg italic text-primaryGray-5">
        <InfoIcon height="24" width="24" />
        No source available
      </div>
    </div>
  );
};
